import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trophy, CheckCircle2, AlertCircle, Download, FileText, Clock, PoundSterling, Save } from "lucide-react";

function prettyBytes(n) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

export default function AdminFinalPitches() {
  const [data, setData] = useState({ deadline_iso: null, teams: [] });
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const [prizes, setPrizes] = useState([
    { rank: 1, amount: 1000, label: "1st place", team_id: null },
    { rank: 2, amount: 500, label: "2nd place", team_id: null },
    { rank: 3, amount: 250, label: "3rd place", team_id: null },
  ]);
  const [prizesLoaded, setPrizesLoaded] = useState(false);
  const [prizesSaving, setPrizesSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: fp }, { data: prog }] = await Promise.all([
          api.get("/admin/final-pitches"),
          api.get("/programme"),
        ]);
        if (cancelled) return;
        setData(fp);
        // Merge saved prizes with defaults (in case only some are set)
        const saved = prog?.pitch_prizes || [];
        setPrizes((defaults) => defaults.map((d) => {
          const hit = saved.find((s) => s.rank === d.rank);
          return hit ? { ...d, ...hit } : d;
        }));
        setPrizesLoaded(true);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Update "past" state once a minute — no need for second-level precision on admin view.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const token = localStorage.getItem("weave_token") || "";
  const downloadUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}/download?auth=${encodeURIComponent(token)}`;

  const deadline = data.deadline_iso ? new Date(data.deadline_iso) : null;
  const past = deadline ? deadline.getTime() < now : false;
  const totalDecks = data.teams.filter((t) => t.deck_file_id).length;

  return (
    <div className="space-y-8" data-testid="admin-final-pitches-page">
      <div>
        <div className="label-eyebrow flex items-center gap-2"><Trophy size={12} /> Admin · final pitch submissions</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Final pitch repository</h1>
        <p className="text-[#525252] mt-3 max-w-3xl text-sm leading-relaxed">
          Every team&apos;s submitted deck and video, in one place. Download each, or build a master deck offline.
        </p>
      </div>

      <div className={`border-2 border-[#0A0A0A] p-5 ${past ? "bg-[#A23232] text-white" : "bg-[#0A0A0A] text-[#f4b847]"}`} data-testid="admin-final-pitches-deadline-card">
        <div className="label-eyebrow opacity-90 flex items-center gap-2"><Clock size={12} /> Deadline</div>
        <div className="font-display text-3xl font-black tracking-tighter mt-1">
          {deadline ? deadline.toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"} BST
        </div>
        <div className="mt-3 text-sm opacity-90">
          {totalDecks} of {data.teams.length} decks submitted
        </div>
      </div>

      {/* Cash prizes */}
      <div className="border-2 border-[#f4b847] bg-[#FEF9E7] p-5 md:p-6" data-testid="admin-pitch-prizes-card">
        <div className="label-eyebrow text-[#7A5B17] flex items-center gap-2"><PoundSterling size={12} /> Cash prizes · Session 8 pitching event</div>
        <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter mt-1 text-[#0F1F2E]">Prize allocation</h2>
        <p className="text-xs text-[#525252] mt-2 max-w-2xl">
          Set the prize amounts (defaults: £1000 / £500 / £250) and pick the winning team for each rank at the end of the pitching event.
          Each prize is split between all members of the winning team. Winners appear on every student&apos;s dashboard once you save.
        </p>
        <div className="mt-4 space-y-3">
          {prizes.sort((a, b) => a.rank - b.rank).map((p, idx) => (
            <div key={p.rank} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center bg-white border border-[#f4b847] p-3" data-testid={`admin-prize-row-${p.rank}`}>
              <div className="md:col-span-2 font-display font-black text-lg text-[#0F1F2E]">
                {p.rank === 1 ? "1st" : p.rank === 2 ? "2nd" : p.rank === 3 ? "3rd" : `${p.rank}th`} place
              </div>
              <div className="md:col-span-3">
                <label className="block text-[10px] uppercase tracking-wider text-[#7A5B17] mb-1">Amount (£)</label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={p.amount}
                  onChange={(e) => setPrizes((prev) => prev.map((x, i) => i === idx ? { ...x, amount: Number(e.target.value) || 0 } : x))}
                  data-testid={`admin-prize-amount-${p.rank}`}
                  className="h-9"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-[10px] uppercase tracking-wider text-[#7A5B17] mb-1">Label</label>
                <Input
                  value={p.label || ""}
                  onChange={(e) => setPrizes((prev) => prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                  placeholder={`${p.rank}${p.rank === 1 ? "st" : p.rank === 2 ? "nd" : "rd"} place`}
                  data-testid={`admin-prize-label-${p.rank}`}
                  className="h-9"
                />
              </div>
              <div className="md:col-span-4">
                <label className="block text-[10px] uppercase tracking-wider text-[#7A5B17] mb-1">Winning team</label>
                <select
                  value={p.team_id || ""}
                  onChange={(e) => setPrizes((prev) => prev.map((x, i) => i === idx ? { ...x, team_id: e.target.value || null } : x))}
                  data-testid={`admin-prize-team-${p.rank}`}
                  className="w-full h-9 border border-slate-300 rounded px-2 text-sm bg-white"
                >
                  <option value="">— Not yet decided —</option>
                  {data.teams.map((t) => (
                    <option key={t.team_id} value={t.team_id}>{t.team_name}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={async () => {
              setPrizesSaving(true);
              try {
                await api.put("/programme", {
                  pitch_prizes: prizes.map((p) => ({
                    rank: p.rank,
                    amount: Number(p.amount) || 0,
                    label: p.label || null,
                    team_id: p.team_id || null,
                  })),
                });
                toast.success("Prizes saved");
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not save prizes");
              } finally {
                setPrizesSaving(false);
              }
            }}
            disabled={prizesSaving || !prizesLoaded}
            data-testid="admin-prize-save-btn"
          >
            <Save className="w-4 h-4 mr-1.5" /> {prizesSaving ? "Saving…" : "Save prizes"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : (
        <div className="space-y-4">
          {data.teams.map((t) => (
            <div key={t.team_id} className="border border-[#0A0A0A] bg-white p-5 md:p-6" data-testid={`admin-final-pitch-team-${t.team_id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="label-eyebrow">{t.team_name}</div>
                  <h2 className="font-display text-2xl font-black tracking-tighter mt-1">{t.team_name}</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    ok={!!t.deck_file_id}
                    label="Deck"
                    testid={`admin-final-pitch-deck-status-${t.team_id}`}
                  />
                </div>
              </div>

              <div className="mt-4">
                <FileBlock
                  icon={FileText}
                  kind="Pitch deck"
                  filename={t.deck_filename}
                  size={t.deck_size}
                  uploadedAt={t.deck_uploaded_at}
                  uploadedBy={t.deck_uploaded_by_name}
                  downloadHref={t.deck_file_id ? downloadUrl(t.deck_file_id) : null}
                  testid={`admin-final-pitch-deck-${t.team_id}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Badge({ ok, label, testid }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 bg-[#0A8C5C] text-white text-[10px] uppercase tracking-wider px-2 py-1 font-semibold" data-testid={testid}>
      <CheckCircle2 size={11} /> {label} submitted
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 bg-[#FEF3D0] border border-[#f4b847] text-[#7A5B17] text-[10px] uppercase tracking-wider px-2 py-1 font-semibold" data-testid={testid}>
      <AlertCircle size={11} /> {label} missing
    </span>
  );
}

function FileBlock({ icon: Icon, kind, filename, size, uploadedAt, uploadedBy, downloadHref, testid }) {
  return (
    <div className={`border ${downloadHref ? "border-[#0A8C5C] bg-[#F4FBF7]" : "border-dashed border-[#E5E7EB] bg-[#FAFAFA]"} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 label-eyebrow">
        <Icon size={12} /> {kind}
      </div>
      {downloadHref ? (
        <>
          <div className="font-medium text-sm mt-2 break-all">{filename}</div>
          <div className="text-xs text-[#525252] mt-0.5">
            {prettyBytes(size)} · uploaded by <strong>{uploadedBy || "—"}</strong>
            {uploadedAt && (<> · {new Date(uploadedAt).toLocaleString()}</>)}
          </div>
          <a
            href={downloadHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 bg-[#0A0A0A] text-[#f4b847] px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#1a1a1a]"
            data-testid={`${testid}-download`}
          >
            <Download size={13} /> Download
          </a>
        </>
      ) : (
        <div className="text-xs text-[#525252] mt-2">Not yet uploaded.</div>
      )}
    </div>
  );
}
